// helix-exception: Helix has no semantic token for these hues (Figma needs-input orange, highlight yellow/amber, categorical teal/cyan); primitive ramps with the identical colour are used (F-11)
// helix-exception: dense badge text drawn at 8-11px in Figma has no Helix type-scale step (F-12)
// helix-exception: inline text highlight marks keep their 1.15px/3px mark radii; no Helix radius token fits mark geometry (F-12)
import clsx from 'clsx'
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { withStopPropagation } from './cardSurface'
import { FormCard, StatusPill } from './FormCard'

/** Card body clip height (R5 ~880:10840); DDx label from Figma Physician Ordering R5 (1403:8754). */
const COLLAPSED_MAX_PX = 160

/** A single HPO term (code + resolved label) that cited a source span. */
export interface HighlightTerm {
  code: string
  label: string
}

/**
 * A source span in the clinical summary that produced one or more extracted
 * HPO terms (GEN-3746). `begin`/`end` are character offsets into `summary`;
 * `terms` are the HPO term(s) that cited this span, shown in the hover/focus
 * tooltip and used to cross-highlight the matching term chip.
 */
export interface SummaryHighlight {
  begin: number
  end: number
  terms: HighlightTerm[]
}

/** An extracted HPO term rendered as a chip below the summary (GEN-3746). */
export interface ExtractedHpoTerm {
  code: string
  label: string
}

interface HighlightInteractions {
  /** HPO code of the currently hovered/focused/pinned term, if any. */
  activeCode: string | null
  /**
   * HPO codes that were extracted from this summary but have since been removed
   * from Indications for Testing (GEN-4571). A span whose contributing terms are
   * *all* removed renders in red so the provider sees the phenotype is no longer
   * on the order even though its source text is still present.
   */
  removedCodes: Set<string>
  /** Activate a mark: highlights the term and anchors the tooltip to the mark. */
  onActivate: (code: string, anchor: HTMLElement, terms: HighlightTerm[]) => void
  onDeactivate: () => void
  onTogglePin: (code: string) => void
  /**
   * Register the DOM node for the mark whose primary term is `code` so the card
   * can scroll it into view when the matching term is activated (GEN-4370).
   * Called with `null` on unmount to release the ref.
   */
  registerMark: (code: string, node: HTMLElement | null) => void
}

/**
 * Render `text` with the given highlight spans wrapped in <mark>. Spans are
 * clamped to the text bounds, sorted, and overlapping spans are merged into a
 * single mark listing every contributing term, so the renderer is robust to
 * PhenoML returning overlapping citations. Each mark carries a styled tooltip
 * (term label + HPO code) and cross-links to its term chip via `interactions`.
 */
function renderHighlightedText(
  text: string,
  highlights: SummaryHighlight[],
  interactions: HighlightInteractions
): ReactNode {
  const spans = highlights
    .filter(
      (h) =>
        Number.isInteger(h.begin) &&
        Number.isInteger(h.end) &&
        h.begin >= 0 &&
        h.end <= text.length &&
        h.end > h.begin
    )
    .sort((a, b) => a.begin - b.begin)

  if (spans.length === 0) {
    return text
  }

  const { activeCode, removedCodes, onActivate, onDeactivate, onTogglePin, registerMark } = interactions
  const nodes: ReactNode[] = []
  let cursor = 0
  let i = 0
  while (i < spans.length) {
    const start = spans[i].begin
    if (start < cursor) {
      // Already covered by a previously emitted (merged) mark.
      i++
      continue
    }
    let end = spans[i].end
    const terms = [...spans[i].terms]
    let j = i + 1
    while (j < spans.length && spans[j].begin < end) {
      end = Math.max(end, spans[j].end)
      terms.push(...spans[j].terms)
      j++
    }
    if (start > cursor) {
      nodes.push(text.slice(cursor, start))
    }
    // De-dupe contributing terms by code, preserving order.
    const seen = new Set<string>()
    const uniqueTerms: HighlightTerm[] = []
    for (const t of terms) {
      if (!seen.has(t.code)) {
        seen.add(t.code)
        uniqueTerms.push(t)
      }
    }
    const codes = uniqueTerms.map((t) => t.code)
    const primaryCode = codes[0]
    const isActive = activeCode != null && codes.includes(activeCode)
    // Red only when *every* contributing term has been removed from the order;
    // a span that still supports at least one active phenotype keeps the normal
    // (amber) highlight (GEN-4571).
    const isRemoved = codes.length > 0 && codes.every((c) => removedCodes.has(c))
    const ariaLabel = uniqueTerms.map((t) => `${t.label} (${t.code})`).join(', ')
    nodes.push(
      // Tooltip is rendered in a portal (see ClinicalSummaryCard) so it is not
      // clipped by the summary body's/card's overflow when it sits above a
      // first-line or edge word.
      <mark
        key={`${start}-${end}`}
        ref={(node) => {
          // Register under every contributing code so activating any of the
          // merged terms can scroll this span into view (GEN-4370).
          for (const c of codes) {
            registerMark(c, node)
          }
        }}
        tabIndex={0}
        role="button"
        aria-label={`Highlighted phenotype source: ${ariaLabel}${
          isRemoved ? ' (removed from order)' : ''
        }`}
        onMouseEnter={(e) => onActivate(primaryCode, e.currentTarget, uniqueTerms)}
        onMouseLeave={onDeactivate}
        onFocus={(e) => onActivate(primaryCode, e.currentTarget, uniqueTerms)}
        onBlur={onDeactivate}
        onClick={(e) => {
          // Stop the click bubbling to the clickable card surface (which would
          // open the section editor); marks are inner interactive controls.
          e.stopPropagation()
          onTogglePin(primaryCode)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onTogglePin(primaryCode)
          }
        }}
        className={clsx(
          'cursor-pointer rounded-[3px] px-0.5 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-brand-600',
          isRemoved
            ? isActive
              ? 'bg-error-100 dark:bg-gray-900 text-text-fg-error-strong dark:text-error-100 line-through decoration-error-500 ring-2 ring-error-500'
              : 'bg-error-100 dark:bg-gray-900 text-text-fg-error-strong dark:text-error-100 line-through decoration-error-500'
            : isActive
              ? 'bg-amber-300 dark:bg-amber-900/40 text-text-body dark:text-gray-400 ring-2 ring-amber-500'
              : 'bg-amber-100 dark:bg-amber-900/40 text-text-body dark:text-gray-400'
        )}
      >
        {text.slice(start, end)}
      </mark>
    )
    cursor = end
    i = j
  }
  if (cursor < text.length) {
    nodes.push(text.slice(cursor))
  }
  return nodes
}

interface ClinicalSummaryCardProps {
  summary: string
  /** Optional differential diagnosis line (GEN-2655). */
  differentialDiagnosis?: string
  /** Optional prior genetic testing line. */
  priorGeneticTesting?: string
  generatedAt?: string
  onEdit?: () => void
  needsAttention?: boolean
  /** R5: required section — show the purple left accent bar even when complete. */
  required?: boolean
  statusMessage?: string
  statusVariant?: 'success' | 'info'
  /** Shown when summary is empty (matches Sequencing Options empty state). */
  emptyStateText?: string
  /**
   * Optional per-term source spans to highlight in the summary (GEN-3746).
   * Offsets are into `summary`. When provided, the highlighted spans show the
   * citing HPO term(s) on hover/focus and cross-link to the term chips.
   */
  highlights?: SummaryHighlight[]
  /**
   * Optional list of extracted HPO terms (GEN-3746) rendered as interactive
   * chips beneath the summary. Hovering/focusing a chip highlights the matching
   * source span(s) and vice versa; clicking pins the selection.
   */
  extractedTerms?: ExtractedHpoTerm[]
  /**
   * HPO codes that were extracted from this summary but are no longer in
   * Indications for Testing (GEN-4571) - e.g. removed manually or dropped during
   * post-submit reconciliation. Their source spans render in red (strikethrough)
   * and their chips are shown struck-through so the provider sees the phenotype
   * is no longer on the order even though the text remains.
   */
  removedCodes?: Iterable<string>
}

export function ClinicalSummaryCard({
  summary,
  differentialDiagnosis,
  priorGeneticTesting,
  generatedAt,
  onEdit,
  needsAttention = false,
  required = false,
  statusMessage,
  statusVariant = 'info',
  emptyStateText = 'None entered',
  highlights,
  extractedTerms,
  removedCodes,
}: ClinicalSummaryCardProps) {
  const hasSummary = summary.trim().length > 0
  const hasHighlights = Boolean(highlights && highlights.length > 0)
  const hasExtractedTerms = Boolean(extractedTerms && extractedTerms.length > 0)
  const removedCodeSet = useMemo(() => new Set(removedCodes ?? []), [removedCodes])
  const hasRemovedTerms = removedCodeSet.size > 0
  const dd = (differentialDiagnosis ?? '').trim()
  const hasDifferential = dd.length > 0
  const pgt = (priorGeneticTesting ?? '').trim()
  const hasPriorGenetic = pgt.length > 0
  const hasAnyBody = hasSummary || hasDifferential || hasPriorGenetic
  const summaryId = useId()
  const bodyRef = useRef<HTMLDivElement>(null)
  // GEN-4370: DOM nodes of the source-span <mark>s, keyed by every HPO code that
  // contributes to each span, so activating a term can scroll its source into
  // view. A span can be registered under several codes when citations overlap.
  const markRefs = useRef<Map<string, HTMLElement>>(new Map())
  const registerMark = (code: string, node: HTMLElement | null) => {
    if (node) {
      markRefs.current.set(code, node)
    } else {
      markRefs.current.delete(code)
    }
  }
  const [isExpanded, setIsExpanded] = useState(false)
  const [contentExceedsLimit, setContentExceedsLimit] = useState(false)
  // GEN-3746: the term (by HPO code) currently hovered/focused (`hoveredCode`)
  // or pinned via click (`pinnedCode`). A pin wins over transient hover so the
  // selection persists while the provider moves between the text and the chips.
  const [hoveredCode, setHoveredCode] = useState<string | null>(null)
  const [pinnedCode, setPinnedCode] = useState<string | null>(null)
  // GEN-3746: the hover/focus tooltip for a source mark, positioned in a portal
  // (viewport coords) so it escapes the summary body's overflow clipping.
  const [tooltip, setTooltip] = useState<{ top: number; left: number; terms: HighlightTerm[] } | null>(null)
  const activeCode = pinnedCode ?? hoveredCode
  const activate = (code: string) => setHoveredCode(code)
  const deactivate = () => setHoveredCode(null)
  // Marks additionally anchor a portal tooltip to the hovered/focused word.
  const activateMark = (code: string, anchor: HTMLElement, terms: HighlightTerm[]) => {
    setHoveredCode(code)
    const rect = anchor.getBoundingClientRect()
    setTooltip({ top: rect.top, left: rect.left + rect.width / 2, terms })
  }
  const deactivateMark = () => {
    setHoveredCode(null)
    setTooltip(null)
  }
  const togglePin = (code: string) => setPinnedCode((prev) => (prev === code ? null : code))

  useEffect(() => {
    setIsExpanded(false)
  }, [summary, differentialDiagnosis, priorGeneticTesting])

  // Reset the active/pinned selection when the summary changes (e.g. after a
  // re-extraction) so a stale code doesn't keep a now-absent term highlighted.
  useEffect(() => {
    setHoveredCode(null)
    setPinnedCode(null)
    setTooltip(null)
  }, [summary])

  useLayoutEffect(() => {
    const el = bodyRef.current
    if (!el || !hasAnyBody) {
      setContentExceedsLimit(false)
      return
    }
    if (isExpanded) {
      return
    }

    const measure = () => {
      setContentExceedsLimit(el.scrollHeight > COLLAPSED_MAX_PX + 1)
    }
    measure()

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    ro?.observe(el)
    window.addEventListener('resize', measure)
    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [summary, differentialDiagnosis, priorGeneticTesting, hasAnyBody, isExpanded])

  // GEN-4370: when a term is activated (chip/mark hover, focus, or pin), scroll
  // its source span into view. If the body is collapsed the mark may be clipped
  // (`overflow-hidden`), so expand first, then scroll on the next frame once the
  // body is scrollable. We only auto-expand when the target is actually clipped,
  // to avoid expanding the card on a casual hover of an already-visible span.
  // GEN-4370: reveal a term's source span, expanding the collapsed body first
  // when the span is clipped (`overflow-hidden`) so it is not hidden, then
  // scrolling it into view. Used by deliberate interactions (pin click and
  // keyboard focus); a casual mouse hover deliberately does NOT call this so it
  // cannot fight a manual "View Less".
  const revealCode = (code: string) => {
    if (!hasHighlights) {
      return
    }
    const mark = markRefs.current.get(code)
    const body = bodyRef.current
    if (!mark || !body) {
      return
    }
    const scrollToMark = () => {
      // `nearest` keeps surrounding context in view; guarded because some
      // environments (older browsers, jsdom) omit scrollIntoView.
      mark.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
    }
    // Is the mark outside the body's currently visible box? Compare rects rather
    // than a magic collapsed-height constant so this is correct regardless of
    // where the span sits (first vs. later paragraph) and of the current
    // collapsed/expanded state. When collapsed the body clips at ~160px, so a
    // span below that has a bottom past the body's bottom edge.
    const bodyRect = body.getBoundingClientRect()
    const markRect = mark.getBoundingClientRect()
    const isHidden = markRect.bottom > bodyRect.bottom + 1 || markRect.top < bodyRect.top - 1
    if (!isExpanded && isHidden) {
      setIsExpanded(true)
      // Wait for the expanded (scrollable) layout to commit before scrolling.
      requestAnimationFrame(scrollToMark)
      return
    }
    scrollToMark()
  }

  // Pinning a term (deliberate click) reveals its source. Keyed off `pinnedCode`
  // only, not transient hover, and guarded by `lastScrolledCodeRef` so a
  // re-render after a manual collapse does not re-expand.
  const lastScrolledCodeRef = useRef<string | null>(null)
  useEffect(() => {
    if (!pinnedCode) {
      lastScrolledCodeRef.current = null
      return
    }
    if (pinnedCode === lastScrolledCodeRef.current) {
      return
    }
    lastScrolledCodeRef.current = pinnedCode
    revealCode(pinnedCode)
    // `revealCode` reads the latest refs/state at call time; re-running only on
    // pin changes is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedCode])

  // Manual expand/collapse is authoritative. On *collapse* ("View Less") clear
  // any pinned/hovered term so the auto-reveal effect does not immediately
  // re-expand the body. On *expand* ("View More") keep the current selection so
  // a pinned term stays highlighted while the provider reads more context
  // (GEN-4370).
  const toggleExpanded = () => {
    if (isExpanded) {
      // Collapsing.
      setPinnedCode(null)
      setHoveredCode(null)
      setTooltip(null)
      lastScrolledCodeRef.current = null
    }
    setIsExpanded((open) => !open)
  }

  const showStatusRow = Boolean(statusMessage || generatedAt)

  return (
    <FormCard
      title="Clinical Summary"
      onEdit={onEdit}
      needsAttention={needsAttention}
      required={required}
      completed={!needsAttention}
      clickableSurface
    >
      {hasAnyBody ? (
        <div className="flex w-full flex-col gap-3">
          <div
            ref={bodyRef}
            id={summaryId}
            className={clsx(
              'flex min-w-0 flex-col gap-3 whitespace-pre-wrap break-words text-sm font-normal leading-5 text-text-body dark:text-gray-400 transition-[max-height] duration-200',
              isExpanded
                ? 'max-h-[min(70vh,48rem)] overflow-y-auto'
                : 'max-h-40 overflow-hidden'
            )}
          >
            {hasSummary ? (
              <div>
                {hasHighlights
                  ? renderHighlightedText(summary, highlights!, {
                      activeCode,
                      removedCodes: removedCodeSet,
                      onActivate: activateMark,
                      onDeactivate: deactivateMark,
                      onTogglePin: togglePin,
                      registerMark,
                    })
                  : summary}
              </div>
            ) : null}
            {hasDifferential ? (
              <div>
                <div className="text-xs font-semibold leading-5 text-text-body-subtle dark:text-gray-500">
                  Differential Diagnosis
                </div>
                <div>{dd}</div>
              </div>
            ) : null}
            {hasPriorGenetic ? (
              <div>
                <div className="text-xs font-semibold leading-5 text-text-body-subtle dark:text-gray-500">
                  Prior Genetic Testing
                </div>
                <div>{pgt}</div>
              </div>
            ) : null}
          </div>
          {contentExceedsLimit ? (
            <div className="flex justify-center">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-full border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-text-body dark:text-gray-400 transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800 focus:outline-none focus:ring-4 focus:ring-brand-600"
                aria-expanded={isExpanded}
                aria-controls={summaryId}
                onClick={withStopPropagation(toggleExpanded)}
              >
                {isExpanded ? 'View Less' : 'View More'}
              </button>
            </div>
          ) : null}
          {hasExtractedTerms ? (
            <div
              className="flex flex-col gap-2"
              // The card is a click-to-edit surface; a click that lands on the
              // chips container/gaps (not a chip button) would otherwise bubble
              // up and open the section editor (GEN-4370). Contain it here so
              // the whole extracted-terms region is inert to the card surface.
              onClick={(e) => e.stopPropagation()}
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-text-body-subtle dark:text-gray-500">
                Extracted HPO Terms
              </p>
              <ul className="flex flex-wrap gap-2">
                {extractedTerms!.map((term) => {
                  const isActive = activeCode === term.code
                  const isRemoved = removedCodeSet.has(term.code)
                  return (
                    <li key={term.code}>
                      <button
                        type="button"
                        onMouseEnter={() => activate(term.code)}
                        onMouseLeave={deactivate}
                        onFocus={() => {
                          activate(term.code)
                          // Keyboard focus is a deliberate navigation: reveal
                          // the source span (GEN-4370). Hover does not.
                          revealCode(term.code)
                        }}
                        onBlur={deactivate}
                        onClick={withStopPropagation(() => togglePin(term.code))}
                        aria-pressed={pinnedCode === term.code}
                        aria-label={isRemoved ? `${term.label} (${term.code}), removed from order` : undefined}
                        className={clsx(
                          'flex items-center gap-2 rounded-full border px-2.5 py-1.5 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-brand-600',
                          isRemoved
                            ? isActive
                              ? 'border-border-error dark:border-error-700 bg-error-500/10 ring-2 ring-error-500/30'
                              : 'border-border-error-subtle dark:border-error-700 bg-error-50 dark:bg-gray-900 hover:bg-error-100 dark:hover:bg-gray-800'
                            : isActive
                              ? 'border-brand-500 bg-brand-600/10 ring-2 ring-brand-500/30'
                              : 'border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-background-secondary-medium dark:hover:bg-gray-800'
                        )}
                      >
                        <span
                          className={clsx(
                            'h-1.5 w-1.5 shrink-0 rounded-full',
                            isRemoved ? 'bg-error-500' : isActive ? 'bg-brand-600' : 'bg-amber-400 dark:bg-amber-900/40'
                          )}
                        />
                        <span className="flex flex-col leading-tight">
                          <span
                            className={clsx(
                              'text-sm font-medium',
                              isRemoved
                                ? 'text-text-fg-error dark:text-error-100 line-through decoration-error-500'
                                : 'text-text-body dark:text-gray-400'
                            )}
                          >
                            {term.label}
                          </span>
                          <span
                            className={clsx(
                              'text-xs',
                              isRemoved
                                ? 'text-text-fg-error/80 dark:text-error-100/80'
                                : 'text-text-body-subtle dark:text-gray-500'
                            )}
                          >
                            {isRemoved ? `${term.code} · removed` : term.code}
                          </span>
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          ) : null}
          {hasHighlights ? (
            <p className="text-xs leading-4 text-text-body-subtle dark:text-gray-500">
              Hover or focus a term to see where it was found in the summary. Click to pin it.
              {hasRemovedTerms ? (
                <>
                  {' '}
                  <span className="text-text-fg-error dark:text-error-100">
                    Terms in red were removed from Indications for Testing and are no longer on the order.
                  </span>
                </>
              ) : null}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-base font-normal leading-6 text-text-body-subtle dark:text-gray-500">{emptyStateText}</p>
      )}

      {showStatusRow ? (
        <>
          <div className="flex flex-col">
            {statusMessage ? (
              <StatusPill
                text={generatedAt ? `${statusMessage} ${generatedAt}` : statusMessage}
                variant={statusVariant}
              />
            ) : (
              generatedAt && <StatusPill text={`Summary generated ${generatedAt}`} variant="success" />
            )}
          </div>
        </>
      ) : null}

      {tooltip
        ? createPortal(
            <div
              role="tooltip"
              className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-base bg-background-dark px-2 py-1 text-xs font-medium text-white shadow-lg"
              style={{ top: tooltip.top - 6, left: tooltip.left }}
            >
              {tooltip.terms.map((t) => (
                <span key={t.code} className="block">
                  {t.label} <span className="text-text-disabled dark:text-gray-300">({t.code})</span>
                </span>
              ))}
            </div>,
            document.body
          )
        : null}
    </FormCard>
  )
}
